/**
 * Event provenance — a read-only, factual record for internal audit.
 *
 * Shows when the event was created, who created it, and what the system does
 * (and does not) record about who configured it. See
 * `services/eventProvenance.service.ts` for the schema evidence behind every
 * field, in particular why there is no "Configured by" attribution.
 *
 * Design rules this surface follows, because it is an audit record:
 *   - Never render a bare UUID with no explanation, and never render blank.
 *   - Never conflate "not recorded", "not visible to you", and "automated".
 *   - Always state the timezone; never show an ambiguous local time.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import {
  getEventProvenance,
  type EventProvenance,
  type ProvenanceActor,
} from "../../services/eventProvenance.service";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Format an instant with an EXPLICIT timezone, e.g.
 * "Sep 09, 2026 at 03:04 PM (America/Chicago)".
 *
 * `formatDateTime` in utils/formatters renders in the viewer's local tz but
 * does not NAME it, which is ambiguous in an audit record — two auditors in
 * different offices would read the same row differently. We name the zone and
 * additionally expose the exact UTC instant via the `title` attribute.
 */
export function formatAuditInstant(value: string | null | undefined): {
  text: string;
  utc: string | null;
} {
  if (!value) {
    return { text: "Not recorded", utc: null };
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return { text: "Not recorded", utc: null };
  }
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    tz = undefined;
  }
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
  return {
    text: `${formatted} (${tz ?? "local time"})`,
    utc: d.toISOString(),
  };
}

/** Short form of a UUID for display; the full value goes in `title`. */
function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export const ActorLabel: React.FC<{ actor: ProvenanceActor }> = ({ actor }) => {
  switch (actor.kind) {
    case "user": {
      const primary = actor.name?.trim() || actor.email?.trim();
      if (!primary) {
        // Profile row exists but carries no name and no email.
        return (
          <span title={actor.userId} className="text-gray-900">
            User {shortId(actor.userId)}{" "}
            <span className="text-gray-500">(no name on profile)</span>
          </span>
        );
      }
      return (
        <span title={actor.userId} className="text-gray-900">
          {primary}
          {actor.name?.trim() && actor.email?.trim() && (
            <span className="text-gray-500"> ({actor.email})</span>
          )}
        </span>
      );
    }
    case "service":
      return (
        <span className="text-gray-900">
          System (automated){" "}
          <span className="text-gray-500">— not a user action</span>
        </span>
      );
    case "restricted":
      return (
        <span title={actor.userId} className="text-gray-900">
          User {shortId(actor.userId)}{" "}
          <span className="text-gray-500">
            — name not visible at your access level
          </span>
        </span>
      );
    case "unrecorded":
    default:
      return <span className="text-gray-500 italic">Not recorded</span>;
  }
};

const Row: React.FC<{
  label: string;
  children: React.ReactNode;
  hint?: string;
}> = ({ label, children, hint }) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-base font-medium text-gray-900 break-words">
      {children}
    </p>
    {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
  </div>
);

const Instant: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { text, utc } = formatAuditInstant(value);
  if (!utc) {
    return <span className="text-gray-500 italic">{text}</span>;
  }
  return <span title={`UTC: ${utc}`}>{text}</span>;
};

/**
 * Pure presentational view. Exported separately from the data-fetching
 * wrapper so it can be rendered in tests with no network and no providers.
 */
export const EventProvenanceView: React.FC<{
  provenance: EventProvenance;
}> = ({ provenance }) => {
  const {
    createdAt,
    createdBy,
    updatedAt,
    setupIncomplete,
    eibLaunch,
    eibLaunchReadable,
  } = provenance;

  return (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <ClipboardDocumentListIcon
          className="h-5 w-5 text-gray-500"
          aria-hidden="true"
        />
        Record
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Read-only provenance for internal audit.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Row label="Created">
          <Instant value={createdAt} />
        </Row>

        <Row label="Created by">
          <ActorLabel actor={createdBy} />
        </Row>

        <Row
          label="Last modified"
          hint="The system does not record which user made the most recent change."
        >
          <Instant value={updatedAt} />
        </Row>

        <Row
          label="Setup state"
          hint="This system does not record who marked the event as set up, or when."
        >
          {setupIncomplete === null ? (
            <span className="text-gray-500 italic">Not recorded</span>
          ) : setupIncomplete ? (
            <span>Setup incomplete</span>
          ) : (
            <span>Marked set up</span>
          )}
        </Row>

        {/*
          The EIB launch commit is the ONE configuration act with a recorded
          actor. It is labelled as exactly what it is — committing the
          Event-in-a-Box launch — and never as "configured the event".
        */}
        {!eibLaunchReadable ? (
          <Row label="Event-in-a-Box launch">
            <span className="text-gray-500 italic">
              Not visible at your access level
            </span>
          </Row>
        ) : eibLaunch ? (
          <>
            <Row label="Event-in-a-Box launch committed">
              <Instant value={eibLaunch.committedAt} />
            </Row>
            <Row label="Launch committed by">
              <ActorLabel actor={eibLaunch.actor} />
            </Row>
          </>
        ) : (
          <Row label="Event-in-a-Box launch">
            <span className="text-gray-500 italic">No committed launch</span>
          </Row>
        )}
      </div>
    </div>
  );
};

/** Data-fetching wrapper used by the Overview tab. */
const EventProvenanceSection: React.FC<{ eventId: string }> = ({ eventId }) => {
  // AuthContext exposes the signed-in user's Profile row as `user` (there is
  // no separate `profile` key), so the role comes straight off it.
  const { user } = useAuth();
  const role = user?.role ?? null;

  const { data, isLoading, error } = useQuery({
    queryKey: ["events", "provenance", eventId, role],
    queryFn: () => getEventProvenance(eventId, role),
    enabled: !!eventId,
  });

  if (isLoading) {
    return (
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Record</h3>
        <p className="text-sm text-gray-500">Loading provenance…</p>
      </div>
    );
  }

  // Fail visibly rather than rendering a half-empty audit record.
  if (error || !data) {
    return (
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Record</h3>
        <p className="text-sm text-gray-500">Provenance could not be loaded.</p>
      </div>
    );
  }

  return <EventProvenanceView provenance={data} />;
};

export default EventProvenanceSection;
